import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import NotFound from '../NotFound/NotFound';
import Loader from '../../components/Loader/Loader';
import LesRetoursDeNosClients from '../../components/les-retours-de-nos-clients/LesRetoursDeNosClients';
import { Helmet } from 'react-helmet-async';
import { Company } from '../../components/les-marques/LesMarques';
import { Splide, SplideSlide } from '@splidejs/react-splide';

export default function LandingPage() {
  const { slug } = useParams();
  const city = slug.match(/casse-auto-(\w+)/)?.[1]?.toLowerCase();
  if (!city) {
    return <NotFound />;
  }

  const [cityData, setCityData] = useState(null);
  const [cities, setCities] = useState([]);
  const [citiesToDisplay, setCitiesToDisplay] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/landing-pages/cities.json')
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        setCities(data);
      })
      .finally(() => {
        setLoadingCities(false);
      });
  }, [city]);

  useEffect(() => {
    if (!cities || !cities.length || loadingCities) {
      return;
    }
    const currentCity = cities.find((cit) => cit.slug === city);
    if (!currentCity) {
      setLoadingData(false);
      return;
    }
    setCitiesToDisplay(
      cities.filter(
        (cit) =>
          cit.slug !== city &&
          cit.enabled &&
          cit.publishedAt <= new Date().toISOString(),
      ),
    );
    fetch(`/landing-pages/data/${city}.json`)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (
          currentCity.enabled &&
          currentCity.publishedAt <= new Date().toISOString()
        ) {
          const years = String(new Date().getFullYear() - 1977);
          const formattedData = JSON.parse(
            JSON.stringify(data).replace(/\$years\$/g, years)
          );
          setCityData(formattedData);
        }
      })
      .finally(() => {
        setLoadingData(false);
      });
  }, [city, cities, loadingCities]);

  const Hero = ({ className = '' }) => {
    return (
      <div className={`content relative flex flex-col items-center justify-center md:h-[333px] px-4 py-8 ${className}`}>
        <div className="absolute rounded-[5px] z-[-1] overflow-hidden w-full h-full">
          <div className="absolute w-full h-full inset-0 bg-opacity-60 z-[1] bg-[#242424]" />
          <img
            className={`absolute w-full min-h-full object-cover opacity-75 z-[0] ${cityData.hero.imageClass}`}
            src={cityData.hero.image}
            alt={`${cityData.primaryKeyword} - casse auto ${cityData.name} - Stop Pièces Auto`}
          />
        </div>
        <div className="max-w-[423px] md:max-w-full leading-[1.2] flex flex-col md:flex-row md:justify-between md:gap-12">
          <div className="flex flex-col items-center md:items-start md:max-w-[480px] md:w-1/2">
            <h1 className="text-[32px] sm:text-[40px] md:text-[42px] lg:text-[48px] font-semibold text-left text-white mb-2 md:text-left">
              Votre casse auto <br/>{cityData.name}
            </h1>
            <p
              className="text-[15px] text-center text-white md:text-left md:font-semibold"
              dangerouslySetInnerHTML={{
                __html: cityData.hero.text,
              }}
            />
            <CatalogCTA
              text="Voir tout notre stock"
              className="hidden md:inline-flex"
            />
          </div>
          <div className="rounded-[18px] bg-[#007fff]/50 p-4 md:px-[30px] md:py-[40px] mt-8 md:mt-0 md:max-w-[533px] md:w-1/2 h-fit">
            <p className="text-lg font-semibold text-center text-white mb-4">
              Trouvez votre pièce en quelques clics grâce à notre
              recherche intelligente
            </p>
            <form
              className="flex justify-start items-center h-[51px] gap-4 px-6 py-4 rounded-lg bg-white border border-[#0d1f22]/[0.12]"
              onSubmit={(e) => {
                e.preventDefault();
                navigate(`/catalogue?q=${searchRef.current.value}`);
              }}
              noValidate={true}
              role="search"
            >
              <input
                type="search"
                aria-label="Search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                className="w-full text-base text-left text-[#0d1f22] focus:outline-none"
                placeholder="Marque, Référence, Modèle.."
                ref={searchRef}
              />
              <button type="submit">
                <svg
                  width={24}
                  height={25}
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-grow-0 flex-shrink-0 w-6 h-6 hover:opacity-100 opacity-70"
                  preserveAspectRatio="none"
                >
                  <g>
                    <path
                      d="M21 21.5L15 15.5M3 10.5C3 11.4193 3.18106 12.3295 3.53284 13.1788C3.88463 14.0281 4.40024 14.7997 5.05025 15.4497C5.70026 16.0998 6.47194 16.6154 7.32122 16.9672C8.1705 17.3189 9.08075 17.5 10 17.5C10.9193 17.5 11.8295 17.3189 12.6788 16.9672C13.5281 16.6154 14.2997 16.0998 14.9497 15.4497C15.5998 14.7997 16.1154 14.0281 16.4672 13.1788C16.8189 12.3295 17 11.4193 17 10.5C17 9.58075 16.8189 8.6705 16.4672 7.82122C16.1154 6.97194 15.5998 6.20026 14.9497 5.55025C14.2997 4.90024 13.5281 4.38463 12.6788 4.03284C11.8295 3.68106 10.9193 3.5 10 3.5C9.08075 3.5 8.1705 3.68106 7.32122 4.03284C6.47194 4.38463 5.70026 4.90024 5.05025 5.55025C4.40024 6.20026 3.88463 6.97194 3.53284 7.82122C3.18106 8.6705 3 9.58075 3 10.5Z"
                      stroke="#FF5600"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const Marques = ({ className = '' }) => {
    const brands = [
      "audi",
      "bmw",
      "peugeot",
      "volkswagen",
      "ford",
      "mercedes",
      "renault",
      "toyota",
      "hyundai",
      "citroen",
      "fiat",
      "kia",
      "mazda",
      "chevrolet",
      "subaru",
      "nissan",
      "mitsubishi",
      "suzuki",
    ];

    return (
      <div className={`content ${className}`}>
        <p
          className="text-base text-center text-zinc-600 sm:text-center"
          dangerouslySetInnerHTML={{
            __html: cityData.marques.title,
          }} 
        />
        <Splide
          className="max-w-[100%] sm:max-w-[calc(9*70px+8*20px)] lg:max-w-[calc(9*80px+8*20px)] xl:max-w-[calc(9*90px+8*20px)] mx-auto mt-4" // Add a custom class to Splide for styling
          options={{
            width: '',
            perPage: 9, // Show only 1 image at a time by default
            perMove: 1, // Move 1 image at a time
            drag: true,
            pagination: false,
            lazyLoad: true,
            speed: 800,
            easing: 'ease-in-out',
            arrows: false, // Set arrows to navigate through the images
            gap: 20,
            padding: 0, // No padding
            autoplay: true,
            interval: 4000,
            pauseOnHover: true,
            type: 'loop',
            autoWidth: true,
            breakpoints: {
              768: {
                gap: 12,
              },
            },
          }}
        >
          {brands.map((brand, index) => (
            <SplideSlide key={index}>
              <Company brand={brand} />
            </SplideSlide>
          ))}
        </Splide>
      </div>
    );
  };

  const NosPiecesAuto = ({ className = '' }) => {
    return (
      <div className={`content ${className}`}>
        <h2
          className="text-3xl font-semibold text-black sm:text-center md:text-4xl md:mb-2"
          dangerouslySetInnerHTML={{
            __html: cityData.nosPiècesAuto.title,
          }}
        />
        <p
          className="text-base text-zinc-600 sm:text-center"
          dangerouslySetInnerHTML={{
            __html: cityData.nosPiècesAuto.subTitle,
          }}
        />
        <div className="mt-5 md:mt-8 w-full grid grid-cols-2 gap-[10px] sm:gap-[20px] md:gap-[30px] lg:gap-[20px] md:grid-cols-4 md:grid-rows-1">
          <Link
            to="/catalogue/vehicules-d-occasion"
            className="bg-gray group rounded-[12px] p-3 lg:p-4 w-full flex flex-col gap-[45px] sm:gap-[120px] 900:gap-[170px] justify-between cursor-pointer relative hover:bg-[#0000005E]"
          >
            <strong className="font-clash font-[600] leading-[21px] text-xl lg:text-2xl lg:leading-[25px] text-dark group-hover:text-white">
              Véhicules <br />
              d'occasion
            </strong>
            <img
              src="/vehicules-occasion.png"
              alt="Véhicules d'occasion à vendre"
              title="Explorez notre sélection de véhicules d'occasion"
              className="absolute right-0 bottom-3 max-w-[60%] max-h-[60%]"
            />
          </Link>
          <Link
            className="bg-gray group rounded-[12px] p-3 lg:p-4 w-full flex flex-col gap-[45px] sm:gap-[120px] 900:gap-[170px] justify-between cursor-pointer relative hover:bg-[#0000005E]"
            to="/catalogue/pieces"
          >
            <strong className="font-clash font-[600] leading-[21px] text-xl lg:text-2xl lg:leading-[25px] text-dark group-hover:text-white">
              Pièces <br /> d'occasion
            </strong>
            <img
              src="/pieces-occasion.png"
              alt="Pièces d'occasion automobile"
              title="Découvrez notre catalogue de pièces d'occasion"
              className="absolute right-0 bottom-3 max-w-[60%] max-h-[60%]"
            />
          </Link>
          <Link
            className="bg-gray group rounded-[12px] p-3 lg:p-4 w-full flex flex-col gap-[45px] sm:gap-[120px] 900:gap-[170px] justify-between cursor-pointer relative hover:bg-[#0000005E]"
            to="/catalogue/jantes"
          >
            <strong className="font-clash font-[600] leading-[21px] text-xl lg:text-2xl lg:leading-[25px] text-dark group-hover:text-white">
              Jantes <br /> d'occasion
            </strong>
            <img
              src="/jantes-occasion.png"
              alt="Jantes d'occasion pour voiture"
              title="Découvrez notre gamme de jantes d'occasion"
              className="absolute right-0 bottom-3 max-w-[60%] max-h-[60%]"
            />
          </Link>
          <Link
            className="bg-gray group rounded-[12px] p-3 lg:p-4 w-full flex flex-col gap-[45px] sm:gap-[120px] 900:gap-[170px] justify-between cursor-pointer relative hover:bg-[#0000005E]"
            to="/catalogue/pneus"
          >
            <strong className="font-clash font-[600] leading-[21px] text-xl lg:text-2xl lg:leading-[25px] text-dark group-hover:text-white">
              Pneus <br /> d'occasion
            </strong>
            <img
              src="/pneus-occasion.png"
              alt="Pneus d'occasion toutes marques"
              title="Explorez notre sélection de pneus d'occasion"
              className="absolute right-0 bottom-3 max-w-[60%] max-h-[60%]"
            />
          </Link>
        </div>
        <div className="mt-6 hidden md:flex justify-center pb-8">
          <CatalogCTA text="Voir tout le catalogue" />
        </div>
      </div>
    );
  };

  const CommentCaMarche = ({ className = '' }) => {
    return (
      <div className={`content text-center ${className}`}>
        <h2 className="text-3xl md:text-4xl mb-4 font-semibold text-black" dangerouslySetInnerHTML={{
          __html: cityData.commentCaMarche.title,
        }} />
        <p className="text-base md:text-lg text-center text-zinc-600" dangerouslySetInnerHTML={{
          __html: cityData.commentCaMarche.subtitle,
        }} />
        <div className="flex flex-col md:flex-row items-center md:items-start gap-10 mt-12 lg:mt-16">
          <div className='flex flex-col gap-4 md:gap-6 lg:gap-8 items-center'>
            <div className='rounded-xl bg-[#d9d9d9] p-3'>
              <svg
                width={60}
                height={60}
                viewBox="0 0 82 82"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <path
                  d="M47.1025 6.48199C49.0532 5.7612 51.1968 5.7612 53.1475 6.48199L69.836 12.6494C70.2161 12.79 70.4687 13.1526 70.4687 13.5581V40.3434C73.1363 42.8312 75.1652 45.9951 76.2812 49.5625V13.5581C76.2812 10.7195 74.5131 8.18129 71.8506 7.19727L55.1621 1.0299C51.9114 -0.171426 48.3386 -0.171426 45.0879 1.0299L28.3993 7.19727C25.7367 8.18129 23.9687 10.7195 23.9687 13.5581V24.3817C25.8938 24.0739 27.8561 24.0739 29.7812 24.3817V13.5581C29.7812 13.1526 30.0338 12.79 30.4142 12.6494L47.1025 6.48199ZM49.624 35.5725C49.3004 35.3869 48.9586 35.2265 48.6006 35.0944L31.9121 28.9269C28.6615 27.7256 25.0885 27.7256 21.8378 28.9269L5.14931 35.0944C2.48672 36.0782 0.71875 38.6164 0.71875 41.4552V63.4264C0.71875 66.2649 2.48672 68.8034 5.14931 69.7872L21.8378 75.9547C25.0885 77.1559 28.6615 77.1559 31.9121 75.9547L42.1371 72.1758C40.5514 70.8273 39.1661 69.2506 38.0311 67.4967L29.8973 70.5026C27.9469 71.2233 25.8031 71.2233 23.8527 70.5026L7.16419 64.3351C6.78382 64.1945 6.53125 63.8318 6.53125 63.4264V41.4552C6.53125 41.0495 6.78382 40.6868 7.16419 40.5465L23.8527 34.379C25.8031 33.6583 27.9469 33.6583 29.8973 34.379L42.8036 39.1484C44.817 37.5705 47.1219 36.3472 49.624 35.5725ZM38.8356 43.214C38.388 43.1478 37.918 43.1854 37.4607 43.3439L26.8734 47.0123L16.2546 43.3419C14.7376 42.8172 13.0827 43.6221 12.5583 45.1391C12.034 46.6562 12.8387 48.3108 14.3557 48.8355L23.9683 52.1579V62.6162C23.9683 64.2212 25.2695 65.5224 26.8746 65.5224C28.4796 65.5224 29.7808 64.2212 29.7808 62.6162V52.1564L35.4031 50.2084C36.1145 47.6509 37.291 45.2872 38.8356 43.214ZM35.8084 18.0141C35.2841 19.5311 36.0886 21.186 37.6056 21.7104L47.9077 25.2713C49.3446 25.768 50.9066 25.7674 52.3434 25.2696L62.6137 21.7108C64.1304 21.1853 64.9337 19.5298 64.4082 18.0133C63.8828 16.4966 62.2274 15.6932 60.7107 16.2187L50.44 19.7775C50.235 19.8486 50.0118 19.8487 49.8065 19.7777L39.5048 16.2168C37.9877 15.6924 36.3327 16.4971 35.8084 18.0141ZM70.1514 66.0378C72.1811 63.1873 73.375 59.7002 73.375 55.9341C73.375 46.3036 65.568 38.4966 55.9375 38.4966C46.307 38.4966 38.5 46.3036 38.5 55.9341C38.5 65.5647 46.307 73.3716 55.9375 73.3716C59.7036 73.3716 63.1907 72.1777 66.0412 70.148L76.1638 80.2707C77.2988 81.4053 79.1387 81.4053 80.2737 80.2707C81.4086 79.1357 81.4086 77.2954 80.2737 76.1605L70.1514 66.0378ZM67.5625 55.9341C67.5625 62.3546 62.358 67.5591 55.9375 67.5591C49.517 67.5591 44.3125 62.3546 44.3125 55.9341C44.3125 49.514 49.517 44.3091 55.9375 44.3091C62.358 44.3091 67.5625 49.514 67.5625 55.9341Z"
                  fill="#212121"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-center text-zinc-900 md:text-2xl">
                {cityData.commentCaMarche.step1.title}
              </h3>
              <p className="text-base text-center text-zinc-700" dangerouslySetInnerHTML={{
                __html: cityData.commentCaMarche.step1.text,
              }}>
              </p>
            </div>
          </div>
          <img src="pictos/Vector 43.svg" className='rotate-180 hidden lg:block w-[120px] xl:w-[160px] h-[84px]' />
          <div className='flex flex-col gap-4 md:gap-6 lg:gap-8 items-center'>
            <div className='rounded-xl bg-[#ff5600] p-3'>
              <svg
                width={60}
                height={60}
                viewBox="0 0 93 93"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className=""
                preserveAspectRatio="none"
              >
                <path
                  d="M41.0405 9.73643C44.542 8.31702 48.458 8.31702 51.9595 9.73643L81.0166 21.5163C83.5756 22.5538 85.25 25.0394 85.25 27.8008V49.6392C83.5283 47.9873 81.5726 46.5776 79.4375 45.4647V30.1195L49.4074 41.7144V50.6196C46.7251 53.4894 44.7063 56.987 43.5949 60.8682V41.7094L13.5625 30.0299V65.2051C13.5625 65.5996 13.8017 65.9545 14.1673 66.1029L43.2245 77.8825C43.3469 77.9325 43.4709 77.9794 43.5949 78.0232V78.0185L44.3261 78.3158C44.5532 78.4084 44.7853 78.4859 45.0197 78.5483C45.9819 80.5881 47.2099 82.4775 48.6592 84.1728C46.1048 84.5564 43.4725 84.2554 41.0405 83.2692L11.9835 71.4896C9.42443 70.4518 7.75 67.9664 7.75 65.2051V27.8008C7.75 25.0394 9.42443 22.5538 11.9835 21.5163L41.0405 9.73643ZM49.7755 15.1231C47.6749 14.2715 45.3251 14.2715 43.2245 15.1231L35.8099 18.1289L64.9776 29.4719L75.3195 25.4787L49.7755 15.1231ZM17.7862 25.4359L46.505 36.6044L56.9303 32.579L27.9582 21.3121L17.7862 25.4359ZM67.8125 89.1274C79.5832 89.1274 89.125 79.5852 89.125 67.8149C89.125 56.0442 79.5832 46.5024 67.8125 46.5024C56.0418 46.5024 46.5 56.0442 46.5 67.8149C46.5 79.5852 56.0418 89.1274 67.8125 89.1274ZM63.9375 72.8249L78.0673 58.6947C78.8241 57.9383 80.0509 57.9383 80.8077 58.6947C81.5641 59.4515 81.5641 60.6783 80.8077 61.4347L65.3077 76.9347C64.5509 77.6915 63.3241 77.6915 62.5673 76.9347L54.8173 69.1847C54.0609 68.4283 54.0609 67.2015 54.8173 66.4447C55.5741 65.6883 56.8009 65.6883 57.5577 66.4447L63.9375 72.8249Z"
                  fill="white"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-center text-zinc-900 md:text-2xl">
                {cityData.commentCaMarche.step2.title}
              </h3>
              <p className="text-base text-center text-zinc-700" dangerouslySetInnerHTML={{
                __html: cityData.commentCaMarche.step2.text,
              }}>
              </p>
            </div>
          </div>
          <img src="pictos/Vector 43.svg" className='hidden lg:block w-[120px] xl:w-[160px] h-[84px] mt-4 lg:mt-6' />
          <div className='flex flex-col gap-4 md:gap-6 lg:gap-8 items-center'>
            <div className='rounded-xl bg-[#d9d9d9] p-3'>
              <svg
                width={60}
                height={60}
                viewBox="0 0 83 82"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className=""
                preserveAspectRatio="none"
              >
                <path
                  d="M0.875 29.2216V13.1562C0.875 10.9547 2.11887 8.94197 4.08807 7.95738L17.6506 1.17613C19.2869 0.357958 21.2131 0.357958 22.8494 1.17613L36.4118 7.95738C38.3811 8.94197 39.625 10.9547 39.625 13.1562V29.2216C39.625 31.4232 38.3811 33.4358 36.4118 34.4204L22.8494 41.2018C21.2131 42.0198 19.2869 42.0198 17.6506 41.2018L4.08807 34.4204C2.11887 33.4358 0.875 31.4232 0.875 29.2216ZM6.89206 13.5412C6.4135 14.4983 6.80143 15.6621 7.75851 16.1406L18.3125 21.4176V32.8139C18.3125 33.884 19.18 34.7515 20.25 34.7515C21.32 34.7515 22.1875 33.884 22.1875 32.8139V21.4176L32.7415 16.1406C33.6986 15.6621 34.0865 14.4983 33.6079 13.5412C33.1294 12.5841 31.9656 12.1962 31.0085 12.6747L20.25 18.054L9.49149 12.6747C8.5344 12.1962 7.37058 12.5841 6.89206 13.5412ZM10.5625 41.9892V62.8445C10.5625 64.3476 11.7037 65.5841 13.167 65.7352C14.7672 61.2282 19.0692 58.0007 24.125 58.0007C29.1866 58.0007 33.4927 61.2356 35.0885 65.7507H44.1615C45.7572 61.2356 50.0635 58.0007 55.125 58.0007V16.3444C55.125 14.7394 53.8238 13.4382 52.2188 13.4382H43.5V13.1555C43.5 11.14 42.8746 9.21939 41.7667 7.62568H52.2188C57.0338 7.62568 60.9375 11.5292 60.9375 16.3444V19.2507H66.9469C70.3162 19.2507 73.3836 21.1921 74.8263 24.237L81.4107 38.1379C81.9632 39.3046 82.25 40.5791 82.25 41.8703V62.8445C82.25 67.6595 78.3463 71.5632 73.5312 71.5632H66.5892C65.6669 77.0611 60.8852 81.2507 55.125 81.2507C49.3648 81.2507 44.5831 77.0611 43.6608 71.5632H35.5893C34.6669 77.0611 29.8852 81.2507 24.125 81.2507C18.3517 81.2507 13.5614 77.0421 12.6545 71.5256C8.22107 71.1149 4.75 67.3852 4.75 62.8445V39.083L10.5625 41.9892ZM60.9375 59.5558C63.3152 60.9314 65.1601 63.1242 66.0885 65.7507H73.5312C75.1363 65.7507 76.4375 64.4495 76.4375 62.8445V61.8757H71.5938C69.9887 61.8757 68.6875 60.5749 68.6875 58.9695C68.6875 57.3644 69.9887 56.0632 71.5938 56.0632H76.4375V44.4382H60.9375V59.5558ZM66.9469 25.0632H60.9375V38.6257H75.2103L69.5733 26.7253C69.0924 25.7103 68.0698 25.0632 66.9469 25.0632ZM18.3125 69.6257C18.3125 72.8358 20.9148 75.4382 24.125 75.4382C27.3352 75.4382 29.9375 72.8358 29.9375 69.6257C29.9375 66.4157 27.3352 63.8132 24.125 63.8132C20.9148 63.8132 18.3125 66.4157 18.3125 69.6257ZM55.125 75.4382C58.335 75.4382 60.9375 72.8358 60.9375 69.6257C60.9375 66.4157 58.335 63.8132 55.125 63.8132C51.915 63.8132 49.3125 66.4157 49.3125 69.6257C49.3125 72.8358 51.915 75.4382 55.125 75.4382Z"
                  fill="#212121"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-center text-zinc-900 md:text-2xl">
                {cityData.commentCaMarche.step3.title}
              </h3>
              <p className="text-base text-center text-zinc-700" dangerouslySetInnerHTML={{
                __html: cityData.commentCaMarche.step3.text,
              }}>
              </p>
            </div>
          </div>
        </div>
        <CatalogCTA text="Je lance ma recherche de pièce auto" to="/catalogue/pieces" className="!mt-12" />
      </div>
    );
  };

  const Engagements = ({ className = '' }) => {
    return (
      <div className={`content flex flex-col sm:flex-row justify-between gap-8 sm:gap-4 md:gap-6 lg:gap-12 ${className}`}>
        <div>
          <p
            className="text-[25px] md:text-[32px] font-semibold text-left text-[#007fff] leading-none mb-2 sm:mb-0 sm:h-[60px] md:h-auto md:mb-5"
            dangerouslySetInnerHTML={{
              __html: cityData.eng1.title,
            }}
          />
          <div>
            <p
              className="text-lg md:text-base font-bold text-left text-zinc-900 mb-1"
              dangerouslySetInnerHTML={{
                __html: cityData.eng1.subtitle,
              }}
            />
            <p
              className="text-[15px] text-left text-zinc-600"
              dangerouslySetInnerHTML={{
                __html: cityData.eng1.text,
              }}
            />
          </div>
        </div>
        <svg
          width={3}
          height={275}
          viewBox="0 0 3 275"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="hidden sm:block md:h-[220px]"
          preserveAspectRatio="none"
        >
          <line
            x1="0.783936"
            y1="2.18213e-8"
            x2="0.783923"
            y2="290.93"
            stroke="#E4E4E7"
          />
        </svg>
        <div>
          <p
            className="text-[25px] md:text-[32px] font-semibold text-left text-[#007fff] leading-none mb-2 sm:mb-0 sm:h-[60px] md:h-auto md:mb-5"
            dangerouslySetInnerHTML={{
              __html: cityData.eng2.title,
            }}
          />
          <div>
            <p
              className="text-lg md:text-base font-bold text-left text-[#101828] mb-1"
              dangerouslySetInnerHTML={{
                __html: cityData.eng2.subtitle,
              }}
            />
            <p className="text-[15px] text-left text-zinc-600"
              dangerouslySetInnerHTML={{
                __html: cityData.eng2.text,
              }}
            />
          </div>
        </div>
        <svg
          width={3}
          height={275}
          viewBox="0 0 3 275"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="hidden sm:block md:h-[220px]"
        >
          <line
            x1="1.12476"
            y1="2.18213e-8"
            x2="1.12474"
            y2="290.93"
            stroke="#E4E4E7"
          />
        </svg>
        <div>
          <p
            className="text-[25px] md:text-[32px] font-semibold text-left text-[#007fff] leading-none mb-2 sm:mb-0 sm:h-[60px] md:h-auto md:mb-5"
            dangerouslySetInnerHTML={{
              __html: cityData.eng3.title,
            }}
          />
          <div>
            <p
              className="text-lg md:text-base font-bold text-left text-zinc-900"
              dangerouslySetInnerHTML={{
                __html: cityData.eng3.subtitle,
              }}
            />
            <p className="text-[15px] text-left text-zinc-600"
              dangerouslySetInnerHTML={{
                __html: cityData.eng3.text,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const PourquoiChoisir = ({ className = '' }) => {
    return (
      <div className={`content flex flex-col md:flex-row gap-1 md:gap-10 xl:gap-40 items-center ${className}`}>
        <div className="max-w-[800px]">
          <Headtitle text={cityData.pourquoiChoisir.headtitle} />
          <h2
            className="text-3xl md:text-4xl font-semibold text-left text-black mb-4 md:mb-6"
            dangerouslySetInnerHTML={{
              __html: cityData.pourquoiChoisir.title,
            }}
          />
          <p
            className="text-[15px] text-left"
            dangerouslySetInnerHTML={{
              __html: cityData.pourquoiChoisir.text
            }}
          />
        </div>

        <div className="mt-[22px] w-max flex flex-col gap-6 justify-evenly items-center text-[15px] sm:text-base md:text-xl">
          <div className="flex items-center gap-2 bg-[#007fff] h-[46px] md:h-[52px] w-full px-5 rounded text-nowrap">
            <img
              src="eng3_transparent.png"
              className="w-[30px] object-cover"
            />
            <p className="font-semibold text-left text-white">
              Made in France
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[#007fff] h-[46px] md:h-[52px] w-full px-5 rounded text-nowrap">
            <img
              src="eng2_transparent_withou_text.png"
              className="w-[30px] object-cover"
            />
            <p className="font-semibold text-left text-white">
              Qualicert SGS+
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[#007fff] h-[46px] md:h-[52px] w-max px-5 rounded text-nowrap">
            <img
              src="eng1_transparent.png"
              className="w-[30px] object-cover"
            />
            <div>
              <p className="font-semibold text-left text-white">
                Centre VHU agréé
              </p>
              <p className="font-semibold text-left text-white text-xs">
                N° d'agrément : PR6900054D
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Rassurance = ({ className = '' }) => {
    return (
      <div className={`w-screen px-1 ${className}`}>
        <div className="border border-[#393939]/40 py-6 md:py-8 px-2 flex flex-col sm:flex-row gap-6 justify-evenly items-center text-[15px] sm:text-base lg:text-2xl max-w-[1450px] mx-auto rounded-lg">
          <div className="flex items-center gap-2">
            <img
              src="pictos/pieces_auto_garantie.svg"
              className="w-[40px] object-cover"
            />
            <p className="font-bold text-left">
              Pièces garantie 12 mois
            </p>
          </div>
          <div className="flex items-center gap-2">
            <img
              src="pictos/paiement_securise.svg"
              className="w-[40px] object-cover"
            />
            <p className="font-bold text-left">
              Paiement 100 % sécurisé
            </p>
          </div>
          <div className="flex items-center gap-2">
            <img
              src="pictos/service_client_tafani.svg"
              className="w-[40px] object-cover"
            />
            <p className="font-bold text-left">
              Service client français
            </p>
          </div>
        </div>
      </div>
    );
  };

  const CitiesToDisplay = ({ className = '' }) => {
    return (
      <div className={`content ${className}`}>
        <h2 className="text-3xl md:text-4xl font-semibold text-left text-black">
          Commandez vos pièces auto en ligne, où que vous soyez
        </h2>
        <div className="flex flex-wrap gap-x-3 gap-y-5 mt-4 md:mt-10">
          {citiesToDisplay.map((city) => (
            <Link
              className="text-xl font-semibold text-center text-white rounded-[20px] bg-[#007fff] px-6 md:px-10"
              key={city.slug}
              to={`/casse-auto-${city.slug}`}
            >
              {city.name}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const ECommerce = ({ className = '' }) => {

    return (
      <div className={`content flex flex-col lg:flex-row gap-6 justify-between ${className}`}>
        <img src={cityData.eCommerce.image} alt={`casse-auto-${cityData.slug}`} className="w-1/3 h-fit max-w-[400px] hidden lg:block" />
        <div className="w-full lg:w-1/2">
          <Headtitle text={cityData.eCommerce.headtitle} />
          <h2
            className="text-3xl md:text-4xl font-semibold text-left text-black"
            dangerouslySetInnerHTML={{
              __html: cityData.eCommerce.title,
            }}
            />
          <p
            className="text-[15px] text-left mt-4 mb-3"
            dangerouslySetInnerHTML={{
              __html: cityData.eCommerce.text,
            }}
          />
          <CatalogCTA text="Je cherche ma pièce détachée" to="/catalogue/pieces" />
        </div>
      </div>
    );
  };

  return (
    <>
      {loadingData && <Loader />}
      {!cityData && !loadingData && <NotFound />}
      {cityData && (
        <>
          <Helmet>
            <title>{cityData.metaTitle} - Stop Pièces Auto</title>
            <meta name="description" content={cityData.metaDescription} />
            <meta
              name="keywords"
              content={
                cityData.primaryKeyword + ', ' + cityData.secondaryKeywords
              }
            />
          </Helmet>
          <div className="wrapper px-[16px] pb-24 md:pb-32">
            <Hero />
            <Marques className="mt-12" />
            <Engagements className="mt-24" />
            <NosPiecesAuto className="mt-24" />
            <PourquoiChoisir className="mt-24" />
            <ECommerce className="mt-24" />
            <Rassurance className="mt-24" />
            <CommentCaMarche className="mt-20" />
            {/* <LesRetoursDeNosClients
              title={`Avis de nos clients sur notre casse auto à ${cityData.name}`}
              reviewClass="w-[150px] sm:w-[286px]"
              className="mt-12 md:mt-16"
              reviews={cityData.reviews}
            /> */}
            {citiesToDisplay.length > 0 && (
              <CitiesToDisplay className="mt-24" />
            )}
          </div>
        </>
      )}
    </>
  );
}

const CatalogCTA = ({ text, className = '', to = '/catalogue' }) => {
  return (
    <Link
      to={to}
      // target="_blank"
      className={`rounded-[32px] bg-[#ff5600] gap-2 px-5 mt-3 inline-flex leading-none ${className}`}
    >
      <p className="text-lg font-semibold text-left text-white">{text}</p>
      <svg
        width={29}
        height={30}
        viewBox="0 0 29 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d="M21.7146 8.81301C21.7146 8.12258 21.1539 7.56195 20.4635 7.56195L13.3935 7.56195C12.7031 7.56195 12.1424 8.12258 12.1424 8.81301C12.1424 9.50344 12.7031 10.0641 13.3935 10.0641L17.4477 10.0641L7.21001 20.3018C6.72118 20.7906 6.72118 21.5804 7.21001 22.0693C7.69883 22.5581 8.48868 22.5581 8.97751 22.0693L19.2152 11.8316L19.2152 15.8858C19.2152 16.5762 19.7758 17.1368 20.4663 17.1368C21.1567 17.1368 21.7173 16.5762 21.7173 15.8858L21.7173 8.81577L21.7146 8.81301Z"
          fill="white"
        />
      </svg>
    </Link>
  );
};

const Headtitle = ({ text, className = '' }) => {
  return (
    <span
      className={`text-[15px] text-left text-blue font-medium mb-3 block ${className}`}
      dangerouslySetInnerHTML={{
        __html: text,
      }}
    />
  );
};